/**
 * API Client - Axios instance with interceptors
 * Handles JWT authentication and standardized response unwrapping
 */

import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { config } from '@config/env';
import type { ApiResponse, ApiError } from '../types/api';
import { useAuthStore } from '../stores/authStore';
import { debugWarn } from '@/utils/logger';
import {
  endRejectedBrowserSession,
  isRefreshSessionRejected,
  markBrowserSessionUnavailable,
  refreshAuthSession,
} from './authSession';

type AuthRetryRequestConfig = InternalAxiosRequestConfig & {
  authRetryAttempted?: boolean;
};

/**
 * Create Axios instance with base configuration
 */
const apiClient = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Request interceptor: Add JWT token to all requests
 */
apiClient.interceptors.request.use(
  (requestConfig) => {
    const { token, actingOrgId, actualOrgId, isImpersonating } = useAuthStore.getState();

    if (
      token &&
      requestConfig.headers &&
      !(requestConfig as AuthRetryRequestConfig).authRetryAttempted
    ) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    if (requestConfig.headers) {
      const actingOrgValue =
        actingOrgId === null || actingOrgId === undefined ? null : String(actingOrgId);
      const actualOrgValue =
        actualOrgId === null || actualOrgId === undefined ? null : String(actualOrgId);

      if (
        actingOrgValue &&
        (isImpersonating ||
          (actualOrgValue && actualOrgValue.length > 0 && actingOrgValue !== actualOrgValue))
      ) {
        requestConfig.headers['X-Acting-Org-ID'] = actingOrgValue;
      }
    }

    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: Handle standardized responses and errors
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Unwrap standardized response format
    // All endpoints return { status, data, message, timestamp, request_id }
    // We extract just the 'data' field for easier use in components
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // Unwrap the data field but maintain the AxiosResponse structure
      const payload = response.data as ApiResponse<unknown>;
      response.data = payload.data ?? null;
      return response;
    }

    // If response doesn't match expected format, return as-is
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.detail || error.message;
    const errorDetailLower = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';

    // Handle authentication failures (401 or 403 with "Not authenticated" message)
    // FastAPI's HTTPBearer returns 403 instead of 401 for missing/invalid tokens
    const isAuthError =
      status === 401 ||
      (status === 403 && errorDetailLower.includes('not authenticated'));

    if (isAuthError) {
      const originalRequest = error.config as AuthRetryRequestConfig | undefined;
      const canRefresh = Boolean(
        originalRequest &&
          !originalRequest.authRetryAttempted &&
          useAuthStore.getState().sessionStatus === 'signed-in',
      );

      if (canRefresh && originalRequest) {
        originalRequest.authRetryAttempted = true;
        try {
          const accessToken = await refreshAuthSession();
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          if (isRefreshSessionRejected(refreshError)) {
            endRejectedBrowserSession();
          } else {
            markBrowserSessionUnavailable();
          }
          return Promise.reject(refreshError);
        }
      }

      const authState = useAuthStore.getState();
      if (
        authState.sessionStatus === 'signed-in' &&
        authState.token &&
        originalRequest?.headers.Authorization === `Bearer ${authState.token}`
      ) {
        endRejectedBrowserSession();
      }
      return Promise.reject(error);
    }

    // Only log errors when debug logging is enabled (exclude auth errors)
    if (status && status >= 400) {
      debugWarn(`API Error ${status}:`, error.config?.url, errorMessage);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
